import type { BranchRow } from "@/modules/branches/domain/types";
import { BranchMapPicker, BranchRadiusSliderField } from "@/modules/branches/presentation/ui/branch-map";
import { isDev } from "@/shared/lib/config";
import { FormRoot, FormInput, FormSelect, FormSwitch, FormTextarea, FormNumber, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";


import { ClockIcon, InfoIcon, MapPinIcon, Wand2Icon } from "lucide-react";
import { z } from "zod";

/** ຊຸດຂໍ້ມູນຕົວຢ່າງ 3 ຊຸດ ສຳລັບປຸ່ມຕື່ມອັດຕະໂນມັດ */
const AUTO_FILL_PRESETS: Partial<BranchFormValues>[] = [
  {
    code: "VT01",
    name: "Mineral Coffee ວຽງຈັນ",
    phone: "+856 21 123 456",
    address: "ຖະໜົນເຊຕະທະຣາດ ເມືອງຈັນທະບູລີ ນະຄອນຫຼວງວຽງຈັນ",
    timezone: "Asia/Vientiane",
    lat: 17.9757,
    lng: 102.6331,
    serviceRadiusM: 5000,
    isActive: true,
    openHours: {
      mon: { enabled: true, open: "07:00", close: "19:00" },
      tue: { enabled: true, open: "07:00", close: "19:00" },
      wed: { enabled: true, open: "07:00", close: "19:00" },
      thu: { enabled: true, open: "07:00", close: "19:00" },
      fri: { enabled: true, open: "07:00", close: "19:00" },
      sat: { enabled: true, open: "08:00", close: "18:00" },
      sun: { enabled: true, open: "08:00", close: "17:00" },
    },
  },
  {
    code: "PK01",
    name: "Mineral Coffee ປາກຊ໋ອງ",
    phone: "+856 31 234 567",
    address: "ເມືອງປາກຊ໋ອງ ແຂວງຈຳປາສັກ (ຕົ້ນທຶນກາເຟອາຣາບິກາປາກຊ໋ອງ)",
    timezone: "Asia/Vientiane",
    lat: 15.1796,
    lng: 106.2307,
    serviceRadiusM: 3000,
    isActive: true,
    openHours: {
      mon: { enabled: true, open: "07:00", close: "19:00" },
      tue: { enabled: true, open: "07:00", close: "19:00" },
      wed: { enabled: true, open: "07:00", close: "19:00" },
      thu: { enabled: true, open: "07:00", close: "19:00" },
      fri: { enabled: true, open: "07:00", close: "19:00" },
      sat: { enabled: true, open: "08:00", close: "18:00" },
      sun: { enabled: true, open: "09:00", close: "16:00" },
    },
  },
  {
    code: "PS01",
    name: "Mineral Coffee ປາກເຊ",
    phone: "+856 31 345 678",
    address: "ທາງເລກ 13 ເມືອງປາກເຊ ແຂວງຈຳປາສັກ",
    timezone: "Asia/Vientiane",
    lat: 15.1161,
    lng: 105.7819,
    serviceRadiusM: 4000,
    isActive: true,
    openHours: {
      mon: { enabled: true, open: "07:00", close: "19:00" },
      tue: { enabled: true, open: "07:00", close: "19:00" },
      wed: { enabled: true, open: "07:00", close: "19:00" },
      thu: { enabled: true, open: "07:00", close: "19:00" },
      fri: { enabled: true, open: "07:00", close: "19:00" },
      sat: { enabled: true, open: "08:00", close: "18:00" },
      sun: { enabled: true, open: "08:00", close: "17:00" },
    },
  },
];

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = {
  mon: "ຈັນ",
  tue: "ອັງຄານ",
  wed: "ພຸດ",
  thu: "ພະຫັດ",
  fri: "ສຸກ",
  sat: "ເສົາ",
  sun: "ອາທິດ",
};

const TIMEZONE_OPTIONS = [
  { value: "Asia/Vientiane", label: "Asia/Vientiane (GMT+7)" },
  { value: "Asia/Bangkok", label: "Asia/Bangkok (GMT+7)" },
  { value: "Asia/Ho_Chi_Minh", label: "Asia/Ho_Chi_Minh (GMT+7)" },
  { value: "Asia/Phnom_Penh", label: "Asia/Phnom_Penh (GMT+7)" },
  { value: "UTC", label: "UTC" },
];

const openHoursEntry = z.object({
  enabled: z.boolean(),
  open: z.string(),
  close: z.string(),
});

const BranchFormSchema = z
  .object({
    code: z.string().min(1, "ຕ້ອງໃສ່ລະຫັດສາຂາ"),
    name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ສາຂາ"),
    phone: z.string().optional(),
    address: z.string().optional(),
    timezone: z.string().min(1).default("Asia/Vientiane"),
    lat: z.union([z.number(), z.nan()]).optional(),
    lng: z.union([z.number(), z.nan()]).optional(),
    serviceRadiusM: z.number().int().min(0).optional(),
    openHours: z.record(z.string(), openHoursEntry),
    isActive: z.boolean(),
    isDistributionHub: z.boolean(),
  })
  .refine(
    (d) => {
      if (d.lat != null && !Number.isNaN(d.lat))
        return d.lat >= -90 && d.lat <= 90;
      return true;
    },
    { message: "Latitude ຕ້ອງຢູ່ລະຫວ່າງ -90 ເຖິງ 90", path: ["lat"] },
  )
  .refine(
    (d) => {
      if (d.lng != null && !Number.isNaN(d.lng))
        return d.lng >= -180 && d.lng <= 180;
      return true;
    },
    { message: "Longitude ຕ້ອງຢູ່ລະຫວ່າງ -180 ເຖິງ 180", path: ["lng"] },
  );

export type BranchFormValues = z.infer<typeof BranchFormSchema>;

function defaultOpenHours(): Record<
  string,
  { enabled: boolean; open: string; close: string }
> {
  const ent = { enabled: true, open: "07:00", close: "19:00" };
  return Object.fromEntries(DAY_KEYS.map((d) => [d, { ...ent }]));
}

function branchToOpenHours(
  openHoursJson: Record<string, { open: string; close: string } | null> | null,
): Record<string, { enabled: boolean; open: string; close: string }> {
  const def = defaultOpenHours();
  if (!openHoursJson) return def;
  return DAY_KEYS.reduce(
    (acc, key) => {
      const v = openHoursJson[key];
      if (v) acc[key] = { enabled: true, open: v.open, close: v.close };
      else acc[key] = { ...def[key], enabled: false };
      return acc;
    },
    {} as Record<string, { enabled: boolean; open: string; close: string }>,
  );
}

function openHoursToJson(
  openHours: Record<string, { enabled: boolean; open: string; close: string }>,
): Record<string, { open: string; close: string } | null> {
  return Object.fromEntries(
    DAY_KEYS.map((key) => {
      const v = openHours[key];
      if (!v || !v.enabled) return [key, null];
      return [key, { open: v.open, close: v.close }];
    }),
  );
}

export type BranchFormSubmitPayload = {
  code: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  serviceRadiusM?: number | null;
  timezone?: string | null;
  openHoursJson: Record<string, { open: string; close: string } | null>;
  isActive: boolean;
  isDistributionHub: boolean;
};

export function BranchForm({
  initialValues,
  onSubmit,
  submitting,
  onCancel,
}: {
  initialValues?: Partial<BranchFormValues> | null;
  onSubmit: (values: BranchFormSubmitPayload) => void;
  submitting?: boolean;
  onCancel?: () => void;
}) {
  const defaultOpen = defaultOpenHours();
  const methods = RHF.useForm<BranchFormValues>({
    resolver: zodResolver(BranchFormSchema),
    defaultValues: {
      code: "",
      name: "",
      phone: "",
      address: "",
      timezone: "Asia/Vientiane",
      lat: undefined,
      lng: undefined,
      serviceRadiusM: undefined,
      openHours: defaultOpen,
      isActive: true,
      isDistributionHub: false,
      ...initialValues,
      openHours: initialValues?.openHours ?? defaultOpen,
    },
  });

  const handleAutoFill = () => {
    const preset =
      AUTO_FILL_PRESETS[Math.floor(Math.random() * AUTO_FILL_PRESETS.length)];
    if (!preset) return;
    (Object.keys(preset) as (keyof BranchFormValues)[]).forEach((key) => {
      const value = preset[key];
      if (value !== undefined) methods.setValue(key, value as never);
    });
  };

  return (
    <FormRoot<BranchFormValues>
      methods={methods}
      onSubmit={(vals) => {
        const lat =
          vals.lat != null && !Number.isNaN(vals.lat) ? vals.lat : null;
        const lng =
          vals.lng != null && !Number.isNaN(vals.lng) ? vals.lng : null;
        onSubmit({
          code: vals.code,
          name: vals.name,
          phone: vals.phone || null,
          address: vals.address || null,
          lat: lat ?? null,
          lng: lng ?? null,
          serviceRadiusM: vals.serviceRadiusM ?? null,
          timezone: vals.timezone || null,
          openHoursJson: openHoursToJson(vals.openHours),
          isActive: vals.isActive,
          isDistributionHub: vals.isDistributionHub,
        });
      }}
      className="space-y-6"
    >
      {/* Card 1: General Information */}
      <Card className="rounded-xl border bg-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <InfoIcon className="h-5 w-5 text-muted-foreground" />
            ຂໍ້ມູນທົ່ວໄປ
          </CardTitle>
          {isDev && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              className="shrink-0 rounded-[12px]"
            >
              <Wand2Icon className="h-4 w-4" />
              ຕື່ມອັດຕະໂນມັດ
            </Button>
          )}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-4">
            <FormInput
              name="code"
              label="ລະຫັດສາຂາ"
              requiredMark
              placeholder="ຕົວຢ່າງ VT01"
            />
            <FormInput
              name="phone"
              label="ເບີໂທ"
              placeholder="+856 21 123 456"
            />
            <FormTextarea
              name="address"
              label="ທີ່ຢູ່ເຕັມ"
              placeholder="ຖະໜົນ..., ເມືອງ..., ແຂວງ..."
              rows={3}
            />
          </div>
          <div className="space-y-4">
            <FormInput
              name="name"
              label="ຊື່ສາຂາ"
              requiredMark
              placeholder="Mineral Coffee ວຽງຈັນ"
            />
            <FormSelect
              name="timezone"
              label="ເວລາທ້ອງຖິ່ນ"
              requiredMark
              options={TIMEZONE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Geographic Settings */}
      <Card className="rounded-xl border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <MapPinIcon className="h-5 w-5 text-muted-foreground" />
            ຕັ້ງຄ່າພູມິສາດ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <FormNumber
              name="lat"
              label="Latitude"
              placeholder="17.9757"
              step={0.0001}
            />
            <FormNumber
              name="lng"
              label="Longitude"
              placeholder="102.6331"
              step={0.0001}
            />
            <div className="relative">
              <FormNumber
                name="serviceRadiusM"
                label="ລັງສີບໍລິການ (ແມັດ)"
                placeholder="5000"
                min={0}
              />
              <span className="absolute top-9 right-3 text-muted-foreground text-sm">
                ມ.
              </span>
            </div>
          </div>
          <BranchRadiusSliderField />
          <BranchMapPicker className="h-[320px]" />
        </CardContent>
      </Card>

      {/* Card 3: Operating Hours */}
      <Card className="rounded-xl border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <ClockIcon className="h-5 w-5 text-muted-foreground" />
            ເວລາເປີດໃຫ້ບໍລິການ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pr-4 pb-2 text-left font-medium text-muted-foreground">
                    ວັນ
                  </th>
                  <th className="pr-4 pb-2 text-left font-medium text-muted-foreground">
                    ສະຖານະ
                  </th>
                  <th className="pr-4 pb-2 text-left font-medium text-muted-foreground">
                    ເວລາເປີດ
                  </th>
                  <th className="pb-2 text-left font-medium text-muted-foreground">
                    ເວລາປິດ
                  </th>
                </tr>
              </thead>
              <tbody>
                {DAY_KEYS.map((key) => (
                  <tr key={key} className="border-b last:border-0">
                    <td className="py-3 pr-4">{DAY_LABELS[key]}</td>
                    <td className="py-3 pr-4">
                      <RHF.Controller
                        name={`openHours.${key}.enabled`}
                        control={methods.control}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            id={`openHours.${key}.enabled`}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </td>
                    <td className="py-3 pr-4">
                      <RHF.Controller
                        name={`openHours.${key}.open`}
                        control={methods.control}
                        render={({ field }) => (
                          <input
                            type="time"
                            className="h-9 w-full max-w-[120px] rounded-[12px] border border-input bg-background px-2 text-sm"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </td>
                    <td className="py-3">
                      <RHF.Controller
                        name={`openHours.${key}.close`}
                        control={methods.control}
                        render={({ field }) => (
                          <input
                            type="time"
                            className="h-9 w-full max-w-[120px] rounded-[12px] border border-input bg-background px-2 text-sm"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Card 4: Active Status + Actions */}
      <Card className="rounded-xl border bg-card">
        <CardContent className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex w-full flex-1 items-center gap-4">
            <div className="text-nowrap">
              <p className="font-medium">ສະຖານະການໃຊ້ງານ</p>
              <p className="text-muted-foreground text-sm">
                ເປີດຫຼືປິດສາຂາ (POS ແລະລະບົບພາຍໃນ). ການຮັບອໍເດີອອນໄລນຕັ້ງໃນໜ້າ ເວລາ & Slot.
              </p>
            </div>
            <div className="">
              <FormSwitch name="isActive" />
            </div>
          </div>
          <div className="mt-4 flex w-full flex-1 items-center gap-4 border-t pt-4">
            <div className="text-nowrap">
              <p className="font-medium">ສາຂາຫຼັກ (Hub / ຄລັງກາງ)</p>
              <p className="text-muted-foreground text-sm">
                ມີໄດ້ 1 ສາຂາ — ໃຊ້ສຳລັບຈັດສົ່ງສຕັອກໄປສາຂາຍ່ອຍ
              </p>
            </div>
            <FormSwitch name="isDistributionHub" />
          </div>

          <div className="flex gap-2">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                ຍົກເລີກ
              </Button>
            )}
            <Button type="submit" isLoading={submitting}>
              {initialValues ? "ບັນທຶກການປ່ຽນແປງ" : "ສ້າງສາຂາ"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </FormRoot>
  );
}

export function branchToFormValues(
  row: BranchRow & {
    openHoursJson?: Record<
      string,
      { open: string; close: string } | null
    > | null;
  },
): Partial<BranchFormValues> {
  return {
    code: row.code,
    name: row.name,
    phone: row.phone ?? "",
    address: row.address ?? "",
    timezone: row.timezone ?? "Asia/Vientiane",
    lat: row.lat ?? undefined,
    lng: row.lng ?? undefined,
    serviceRadiusM: row.serviceRadiusM ?? undefined,
    openHours: branchToOpenHours(row.openHoursJson ?? null),
    isActive: row.isActive,
    isDistributionHub: row.isDistributionHub ?? false,
  };
}
